import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { addToQueue, getIsRefreshing, processQueue, setIsRefreshing } from './authQueue'
import { clearTokens, getCustomAccessToken, refreshCustomAccessToken } from './utilities'

// Extend Axios request config to include retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean
}

const baseUrl = process.env.react_app_rest_url || process.env.REACT_APP_REST_URL

// Create an Axios instance with base configuration
const axiosInstance = axios.create({
	baseURL: baseUrl,
})

/**
 * Request interceptor - adds bearer token to all outgoing requests
 */

axiosInstance.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		const token = await getCustomAccessToken()

		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}

		return config
	},
	error => Promise.reject(error)
)

/**
 * Handles token refresh and request retry logic
 */
const handleTokenRefresh = async (originalRequest: ExtendedAxiosRequestConfig) => {
	// Mark this request as retrying and lock the refresh process
	originalRequest._retry = true
	setIsRefreshing(true)

	try {
		const result = await refreshCustomAccessToken()

		if (!result.ok) {
			processQueue(new Error('Token refresh failed'), null)
			if (result.isAuthFailure) {
				await clearTokens()
				window.location.href = '/'
			}
			return Promise.reject(new Error('Token refresh failed'))
		}

		// Release the new token to all queued requests
		processQueue(null, result.accessToken)

		// Retry the original request with the new token
		originalRequest.headers.Authorization = `Bearer ${result.accessToken}`
		return axiosInstance(originalRequest)
	} finally {
		// Unlock the refresh process
		setIsRefreshing(false)
	}
}

/**
 * Checks if the request should skip token refresh logic
 */
const shouldSkipRefresh = (url?: string): boolean => {
	return url?.includes('auth.refresh') || url?.includes('auth.login') || false
}

/**
 * Response interceptor - handles 401 errors with automatic token refresh
 */
axiosInstance.interceptors.response.use(
	response => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as ExtendedAxiosRequestConfig

		// Check if this is a 401 error that should trigger token refresh
		const shouldRefresh =
			error.response?.status === 401 &&
			!originalRequest._retry &&
			!shouldSkipRefresh(originalRequest.url)

		if (!shouldRefresh) {
			return Promise.reject(error)
		}

		// If a refresh is already in progress, queue this request
		if (getIsRefreshing()) {
			try {
				const token = await addToQueue()

				originalRequest.headers.Authorization = `Bearer ${token}`
				return axiosInstance(originalRequest)
			} catch (err) {
				return Promise.reject(err)
			}
		}

		// Otherwise, initiate token refresh
		return handleTokenRefresh(originalRequest)
	}
)

export default axiosInstance
